import styled from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { Section } from "../../components/ui/Section";
import { StaggerReveal, MotionItem } from "../../components/ui/Motion";

// =====================
// HERO
// =====================
export const PageHeader = styled.div`
  position: relative;
  padding-top: calc(4rem + 60px);

  padding-bottom: ${({ theme }) => theme.spacing["3xl"]};
  background: ${({ theme }) => theme.colors.navy};
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 50% 0%,
        rgba(112, 141, 171, 0.18) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 15% 100%,
        rgba(201, 169, 110, 0.08) 0%,
        transparent 45%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, ${({ theme }) => theme.fontSizes["6xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};

  em {
    font-style: italic;
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

export const HeaderSub = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.55);
  max-width: 560px;
  margin: 0 auto;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

// =====================
// MISSION
// =====================
export const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`;

export const MissionImageWrap = styled(MotionItem)`
  aspect-ratio: 3 / 2;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    aspect-ratio: 16 / 9;
  }
`;

export const MissionImageInner = styled(motion.div)`
  position: absolute;
  inset: -20%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.pearl} 0%,
    ${({ theme }) => theme.colors.lightGray} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.midGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  will-change: transform;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const MissionContent = styled(StaggerReveal)``;

export const MissionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.75rem, 3vw, ${({ theme }) => theme.fontSizes["4xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: ${({ theme }) => theme.lineHeights.tight};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const MissionDivider = styled.div`
  width: 48px;
  height: 2px;
  background: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const MissionText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

// =====================
// APPROACH (centered)
// =====================
export const ApproachWrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

export const ApproachBody = styled(StaggerReveal)`
  text-align: center;
`;

export const ApproachText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

// Two-column variant: editorial copy beside a supporting image.
export const ApproachGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`;

export const ApproachContent = styled(StaggerReveal)`
  text-align: left;

  ${ApproachText} {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

export const ApproachImageWrap = styled(MotionItem)`
  /* Match the source 3:2 landscape so the image isn't cropped or upscaled */
  aspect-ratio: 3 / 2;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.lg};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// =====================
// NAVY SECTION (with subtle glow), used by Stats & Team
// =====================
export const NavySection = styled(Section).attrs({ $bg: "navy" })`
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse at 80% 30%,
        rgba(112, 141, 171, 0.14) 0%,
        transparent 50%
      ),
      radial-gradient(
        ellipse at 20% 90%,
        rgba(201, 169, 110, 0.06) 0%,
        transparent 50%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
`;

// =====================
// STATS
// =====================
export const StatsGrid = styled(StaggerReveal)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

export const StatCard = styled(MotionItem)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 240px;
  padding: ${({ theme }) => `${theme.spacing["3xl"]} ${theme.spacing.xl}`};

  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(12px);
  transition: all ${({ theme }) => theme.transitions.normal};
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0 14px 36px rgba(0, 0, 0, 0.32);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-height: 200px;
    padding: ${({ theme }) => `${theme.spacing["2xl"]} ${theme.spacing.lg}`};
  }
`;

export const StatNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.75rem, 5vw, 4rem);
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &::after {
    content: "";
    display: block;
    width: 32px;
    height: 1px;
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.5;
    margin: ${({ theme }) => `${theme.spacing.lg} auto 0`};
  }
`;

export const StatLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

// =====================
// VALUES
// =====================
export const ValuesGrid = styled(StaggerReveal)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ValueIconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.pearl};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid rgba(112, 141, 171, 0.28);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
  }
`;

export const ValueCard = styled(MotionItem)`
  position: relative;
  padding: ${({ theme }) => theme.spacing["2xl"]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.normal};
  -webkit-tap-highlight-color: transparent;

  /* Hover only on devices that actually hover (mouse/trackpad) — on touch
     devices tapping a card must not leave it stuck in a hover/focus state. */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.lg};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

export const ValueTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const ValueDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

// =====================
// TEAM
// =====================
export const TeamGrid = styled(StaggerReveal)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

export const TeamCard = styled(MotionItem)`
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(12px);
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    border-color: rgba(112, 141, 171, 0.4);
    background: rgba(255, 255, 255, 0.07);
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  }
`;

export const TeamPhoto = styled.div`
  aspect-ratio: 1;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primaryDark} 0%,
    ${({ theme }) => theme.colors.navy} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TeamInitials = styled.span`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["5xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: rgba(255, 255, 255, 0.3);
`;

export const TeamInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

export const TeamName = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const TeamRole = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

// =====================
// TIMELINE
// =====================
export const TimelineWrap = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: ${({ theme }) => theme.colors.lightGray};
    transform: translateX(-50%);

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      left: 24px;
    }
  }
`;

export const TimelineItem = styled(MotionItem)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing["2xl"]};
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }

  &:nth-child(even) {
    flex-direction: row-reverse;
    text-align: right;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      flex-direction: row;
      text-align: left;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: 56px;
    gap: 0;
  }
`;

export const TimelineDot = styled.div`
  position: absolute;
  left: 50%;
  top: 6px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  border: 3px solid ${({ theme }) => theme.colors.offWhite};
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.colors.lightGray},
    0 0 0 6px rgba(112, 141, 171, 0.08);
  transform: translateX(-50%);
  z-index: 1;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    left: 24px;
  }
`;

export const TimelineContent = styled.div`
  flex: 1;
  max-width: calc(50% - 32px);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

export const TimelineYear = styled.p`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["3xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.light};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const TimelineTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const TimelineDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

// =====================
// CTA
// =====================
export const CtaWrap = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing["2xl"]} 0;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 720px;
    height: 720px;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      circle,
      rgba(112, 141, 171, 0.14) 0%,
      rgba(112, 141, 171, 0.05) 35%,
      transparent 65%
    );
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

export const CtaSection = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto;
`;

export const CtaTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["4xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  em {
    font-style: italic;
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

export const CtaText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing["2xl"]};
`;

export const CtaButton = styled(Link)`
  display: inline-block;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  padding: 16px 48px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(112, 141, 171, 0.35);
  }
`;

// =====================
// SHARED
// =====================
export const SectionHeader = styled(StaggerReveal)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing["3xl"]};
`;

export const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["5xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: inherit;
`;
